import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Modal,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { RHFTextField, RHFSelect } from '../../../components/hook-form/index';
import axios from '../../../utils/axios';
import modifyScriptName from '../../../utils/modifyScriptName';
import { Wrapper } from '../../../sections/@dashboard/Trade/watchlist/BuyScriptDrawer';

// Constants for select fields
const typeOptions = [
  { label: 'OPEN', value: 'OPEN' },
  { label: 'CLOSE', value: 'CLOSE' },
  { label: 'ORDER', value: 'ORDER' },
];

const sideOptions = [
  { label: 'BUY', value: 'BUY' },
  { label: 'SELL', value: 'SELL' },
];

const boughtTypeOptions = [
  { label: 'MARKET', value: 'MARKET' },
  { label: 'CF', value: 'CF' },
  { label: 'LIMIT', value: 'LIMIT' },
  { label: 'SETTLEMENT', value: 'SETTLEMENT' },
];

const soldTypeOptions = [
  { label: 'MARKET', value: 'MARKET' },
  { label: 'CF', value: 'CF' },
  { label: 'LIMIT', value: 'LIMIT' },
  { label: 'SETTLEMENT', value: 'SETTLEMENT' },
];

// Validation schema using yup
const schema = yup.object().shape({
  type: yup.string().required('Type is required'),
  side: yup.string().required('Side is required'),
  quantity: yup.number()
    .when('type', {
      is: (type) => type !== 'CLOSE',
      then: yup
        .number()
        .typeError('Quantity is required')
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .min(1, 'Quantity must be at least 1')
        .integer('Quantity must be an integer'),
      otherwise: (schema) => schema,
    }), buyQuantity: yup
      .number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .when(['type', 'side'], {
        is: (type, side) => (type === 'OPEN' || type === 'CLOSE') && side === 'BUY',
        then: yup.number().required('Buy Quantity is required').min(0, 'Buy Quantity must be at least 0').integer('Quantity must be an integer'),
        otherwise: yup.number().nullable().min(0, 'Buy Quantity must be at least 0').integer('Quantity must be an integer'),
      }),
  sellQuantity: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when(['type', 'side'], {
      is: (type, side) => (type === 'OPEN' || type === 'CLOSE') && side === 'SELL',
      then: yup.number().required('Sell Quantity is required').min(0, 'Sell Quantity must be at least 0').integer('Quantity must be an integer'),
      otherwise: yup.number().nullable().min(0, 'Sell Quantity must be at least 0').integer('Quantity must be an integer'),
    }),
  buy: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when(['side', 'type'], {
      is: (side, type) => side === 'BUY' && (type === 'OPEN' || type === 'CLOSE'),
      then: yup.number().required('Buy is required').min(0.01, 'Buy must be at least 0.01'),
      otherwise: (schema) => schema,
    })
    .when(['side', 'type'], {
      is: (side, type) => side === 'SELL' && (type !== 'ORDER'),
      then: yup.number().transform(value => (value ? 0 : 0)).test('is-zero', 'Buy must be zero when side is SELL', value => value === 0 || value === null || value === ''),
    }),
  sell: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when(['side', 'type'], {
      is: (side, type) => side === 'SELL' && (type === 'OPEN' || type === 'CLOSE'),
      then: yup.number().required('Sell is required').min(0.01, 'Sell must be at least 0.01'),
      otherwise: (schema) => schema,
    })
    .when(['side', 'type'], {
      is: (side, type) => side === 'BUY' && (type !== 'ORDER'),
      then: yup.number().transform(value => (value ? 0 : 0)).test('is-zero', 'Sell must be zero when side is BUY', value => value === 0 || value === null || value === ''),
    }), boughtAt: yup.date()
      .when(['type', 'side'], {
        is: (type, side) => type === 'ORDER' || side === 'BUY',
        then: yup.date().required('Bought At is required').nullable(),
        otherwise: yup.date().nullable(),
      }),
  soldAt: yup.date()
    .when(['type', 'side'], {
      is: (type, side) => type === 'ORDER' || side === 'SELL',
      then: yup.date().required('Sold At is required').nullable(),
      otherwise: yup.date().nullable(),
    }),
  boughtType: yup.string().when(['type', 'side'], {
    is: (type, side) => type === 'ORDER' || (type === 'OPEN' && side === 'BUY'),
    then: yup.string().required('Bought type is required'),
    otherwise: yup.string().nullable(),
  }), soldType: yup.string()
    .when(['type', 'side'], {
      is: (type, side) =>
        type === 'ORDER' ||
        ((type === 'OPEN' || type === 'CLOSE') && side === 'SELL'),
      then: yup.string().required('Sold type is required')
        .when(['type', 'boughtType'], {
          is: (type, boughtType) =>
            type === 'ORDER' &&
            (boughtType === 'CF' || boughtType === 'SETTLEMENT'),
          then: (schema) =>
            schema.notOneOf(
              [yup.ref('boughtType')],
              'For ORDER trades, boughtType and soldType cannot be the same when using CF or SETTLEMENT'
            ),
        }),
      otherwise: yup.string().nullable(),
    }),
});

const SuperAdminEditTradeModal = ({ open, trade, onClose, onUserUpdate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);  // Function to format date to YYYY-MM-DDThh:mm:ss format required by datetime inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // First create a date object from the string
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    // Convert to IST (Indian Standard Time) using offset method
    // IST is UTC+5:30 (330 minutes)
    const istOffset = 330; // 5 hours and 30 minutes in minutes

    // Get UTC time in milliseconds
    const utcTime = date.getTime();

    // Create a new date object with IST offset applied
    const dateInIST = new Date(utcTime + (istOffset * 60 * 1000));

    // Format to YYYY-MM-DDThh:mm:ss
    return dateInIST.toISOString().slice(0, 19);
  };  // Initialize the form with trade data
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: trade.type || '',
      side: trade.side || '',
      quantity: trade.quantity || trade.buyQuantity || trade.sellQuantity || 0,
      buyQuantity: trade.buyQuantity || 0,
      sellQuantity: trade.sellQuantity || 0,
      buy: trade.buy || null,
      sell: trade.sell || null,
      boughtType: trade.boughtType || '',
      soldType: trade.soldType || '',
      boughtAt: formatDateForInput(trade.boughtAt),
      soldAt: formatDateForInput(trade.soldAt),
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
    watch,
  } = methods;

  console.log("errors", methods.formState.errors);

  // Watch for form value changes
  const watchType = watch('type');

  // Watch for changes in the form
  useEffect(() => {
    const subscription = methods.watch((value, { type }) => {
      if (type === 'change') {
        setIsDirty(Object.keys(methods.formState.dirtyFields).length > 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);  // Reset form when trade data changes or modal opens/closes
  useEffect(() => {
    if (open && trade) {
      reset({
        type: trade.type || '',
        side: trade.side || '',
        quantity: trade.quantity || trade.buyQuantity || trade.sellQuantity || 0,
        buyQuantity: trade.buyQuantity || 0,
        sellQuantity: trade.sellQuantity || 0,
        buy: trade.buy || null,
        sell: trade.sell || null,
        boughtType: trade.boughtType || '',
        soldType: trade.soldType || '',
        boughtAt: trade.boughtAt ? formatDateForInput(trade.boughtAt) : null,
        soldAt: trade.soldAt ? formatDateForInput(trade.soldAt) : null,
      });
      setIsDirty(false);
    }
  }, [open, reset, trade]);

  // State to manage warning popup
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessages, setWarningMessages] = useState([]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const res = await axios.put(`/trade/superadmin/edit/${trade._id}`, data);

      // Check for warnings
      if (res.data?.error?.hasWarning) {
        setWarningMessages(res.data.error.warnings);
        setShowWarning(true);
      } else {
        enqueueSnackbar(res.data.message || 'Trade updated successfully', { variant: 'success' });
        onUserUpdate();
        onClose();
      }
    } catch (error) {
      if (error?.hasWarning) {
        setWarningMessages(error.warnings);
        setShowWarning(true);
      } else {
        enqueueSnackbar(`Failed to edit trade: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWarningConfirm = async () => {
    try {
      setLoading(true);
      const data = methods.getValues();
      const res = await axios.put(`/trade/superadmin/edit/${trade._id}`, { ...data, force: true });
      enqueueSnackbar(res.data.message || 'Trade updated successfully', { variant: 'success' });
      setShowWarning(false);
      onUserUpdate();
      onClose();
    } catch (error) {
      enqueueSnackbar(`Failed to edit trade: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
  };

  console.log("data", methods.getValues());

  const scriptName = trade.script?.tradingsymbol ? modifyScriptName(trade.script?.tradingsymbol) : 'Unknown Script';

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '100%', sm: 500 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" textAlign="center" pb={2}>
            Super Admin Edit Trade - {scriptName}
          </Typography>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>                {/* Trade Type and Side */}
                <Grid item xs={6}>
                  <RHFSelect name="type" label="Trade Type" fullWidth disabled>
                    {typeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </RHFSelect>
                </Grid>

                <Grid item xs={6}>
                  <RHFSelect name="side" label="Side" fullWidth>
                    {sideOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </RHFSelect>                </Grid>
                {/* Quantity - Only show for OPEN and ORDER types */}
                {watchType !== 'CLOSE' && (
                  <Grid item xs={12}>
                    <RHFTextField name="quantity" label="Quantity" type="number" fullWidth />
                  </Grid>
                )}

                {/* Buy Quantity or Sell Quantity based on side (Only for OPEN/CLOSE trades) */}
                {(watchType === 'OPEN' || watchType === 'CLOSE') && (
                  <>
                    {watch('side') === 'BUY' && (
                      <Grid item xs={12}>
                        <RHFTextField name="buyQuantity" label="Buy Quantity" type="number" fullWidth />
                      </Grid>
                    )}
                    {watch('side') === 'SELL' && (
                      <Grid item xs={12}>
                        <RHFTextField name="sellQuantity" label="Sell Quantity" type="number" fullWidth />
                      </Grid>
                    )}
                  </>
                )}
                {/* Buy and Sell - Conditionally displayed for OPEN/CLOSE trades */}
                {watchType === 'ORDER' ? (
                  <>
                    <Grid item xs={6}>
                      <RHFTextField name="buy" label="Buy" type="number" fullWidth />
                    </Grid>
                    <Grid item xs={6}>
                      <RHFTextField name="sell" label="Sell" type="number" fullWidth />
                    </Grid>
                  </>
                ) : (
                  <>
                    {watch('side') === 'BUY' && (
                      <Grid item xs={12}>
                        <RHFTextField name="buy" label="Buy" type="number" fullWidth />
                      </Grid>
                    )}
                    {watch('side') === 'SELL' && (
                      <Grid item xs={12}>
                        <RHFTextField name="sell" label="Sell" type="number" fullWidth />
                      </Grid>
                    )}
                  </>
                )}

                {/* Dates - Conditionally displayed for OPEN/CLOSE trades */}
                {watchType === 'ORDER' ? (
                  <>
                    <Grid item xs={6}>
                      <RHFTextField
                        name="boughtAt"
                        label="Bought At (YYYY-MM-DD HH:MM:SS)"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 1 }} // Enable seconds in the input
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <RHFTextField
                        name="soldAt"
                        label="Sold At (YYYY-MM-DD HH:MM:SS)"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 1 }} // Enable seconds in the input
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    {watch('side') === 'BUY' && (
                      <Grid item xs={12}>
                        <RHFTextField
                          name="boughtAt"
                          label="Bought At (YYYY-MM-DD HH:MM:SS)"
                          type="datetime-local"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 1 }} // Enable seconds in the input
                        />
                      </Grid>
                    )}
                    {watch('side') === 'SELL' && (
                      <Grid item xs={12}>
                        <RHFTextField
                          name="soldAt"
                          label="Sold At (YYYY-MM-DD HH:MM:SS)"
                          type="datetime-local"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 1 }} // Enable seconds in the input
                        />
                      </Grid>
                    )}
                  </>
                )}                {/* Bought Type and Sold Type */}
                {(watchType === 'ORDER') && (
                  <>
                    <Grid item xs={6}>
                      <RHFSelect name="boughtType" label="Bought Type" fullWidth>
                        {boughtTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={6}>
                      <RHFSelect name="soldType" label="Sold Type" fullWidth>
                        {soldTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </RHFSelect>
                    </Grid>
                  </>
                )}

                {/* BoughtType for OPEN trades with BUY side */}
                {(watchType === 'OPEN' && watch('side') === 'BUY') && (
                  <Grid item xs={12}>
                    <RHFSelect name="boughtType" label="Bought Type" fullWidth>
                      {boughtTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </RHFSelect>
                  </Grid>
                )}

                {/* SoldType for OPEN/CLOSE trades with SELL side */}
                {((watchType === 'OPEN' || watchType === 'CLOSE') && watch('side') === 'SELL') && (
                  <Grid item xs={12}>
                    <RHFSelect name="soldType" label="Sold Type" fullWidth>
                      {soldTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </RHFSelect>
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    size="large"
                    sx={{ textTransform: 'uppercase' }}
                    loading={isSubmitting}
                    disabled={!isDirty}
                  >
                    Update
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Box>
      </Modal>

      {/* Warning Dialog */}
      <Dialog open={showWarning}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          {warningMessages.map((message, index) => (
            <Typography key={index} sx={{ mb: 1 }}>
              {message}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel} color="secondary">
            Cancel
          </Button>
          <LoadingButton onClick={handleWarningConfirm} color="primary" loading={loading} variant="contained">
            Proceed Anyway
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

SuperAdminEditTradeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  trade: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func,
};

export default SuperAdminEditTradeModal;
